import React, { useCallback, useEffect } from 'react';
import type { FC } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl-next';
import { styled } from '@compiled/react';

import Button from '@atlaskit/button';
import Heading from '@atlaskit/heading';
import CloseIcon from '@atlaskit/icon/core/close';
import Modal from '@atlaskit/modal-dialog';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { token } from '@atlaskit/tokens';
import { N500 } from '@atlaskit/theme/colors';
import { Box, Flex, xcss, Text } from '@atlaskit/primitives';
import { LoomLogo } from '@atlaskit/logo';
import { useIsReducedMotion } from '@atlaskit/motion';

import { ADMIN_NEW_FEATURES } from '@confluence/named-routes';
import { getBuildInfo } from '@confluence/build-info';

import staticAdminOptInImg from './assets/admin-optin.png';

const i18n = defineMessages({
	modalDescription: {
		id: 'beta-features.loom-modal.modal-description',
		defaultMessage: 'Help your team to do their best work',
		description: 'Text shown to describe what Loom does as a product.',
	},
	modalPrompt: {
		id: 'beta-features.loom-modal.modal-prompt',
		defaultMessage:
			'Record and share Loom videos directly within Confluence to enable more effective communication for everyone',
		description:
			'Text shown to describe what Loom and Confluence work together when they enable the new feature',
	},
	enableButtonText: {
		id: 'beta-features.loom-modal.enable-button-text',
		defaultMessage: 'Get started',
		description:
			'Appears on a button that the user can click to take them to their settings page where they can enable.',
	},
	xButtonLabel: {
		id: 'beta-features.loom-modal.x-button-label',
		defaultMessage: 'Close modal',
		description:
			'ARIA label for button letting the user know that clicking the X will close the modal.',
	},
});

const centeredText = xcss({
	textAlign: 'center',
});

const mediaWrapper = xcss({
	height: '320px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
const SplashVideo = styled.video({
	height: '100%',
	objectFit: 'cover',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
const SplashImage = styled.img({
	height: '100%',
	width: '100%',
	objectFit: 'cover',
});

export type LoomBetaModalProps = {
	onClose?: () => void;
};

export const LoomBetaAdminOptInOnboardingModal: FC<LoomBetaModalProps> = ({ onClose }) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();
	const { PUBLIC_PATH } = getBuildInfo();
	const isReducedMotion = useIsReducedMotion();

	const closeModal = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				action: 'closed',
				actionSubject: 'loomBetaOptInModal',
				actionSubjectId: 'loomBetaOptInModalClosed',
				source: 'loomBetaOptInModalScreen',
			},
		}).fire();
		onClose?.();
	}, [createAnalyticsEvent, onClose]);

	useEffect(() => {
		createAnalyticsEvent({
			type: 'sendScreenEvent',
			data: {
				source: 'loomBetaOptInModalScreen',
				name: 'loomBetaOptInModal',
			},
		}).fire();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleEnableClick = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'enableSetting',
				source: 'loomBetaOptInModalScreen',
			},
		}).fire();
		onClose?.();
	};

	return (
		<Modal onClose={closeModal} shouldScrollInViewport testId="loom-beta-opt-in">
			<Box padding="space.100" paddingBlockEnd="space.050">
				<Flex justifyContent="end">
					<Button appearance="link" onClick={closeModal} spacing="none">
						<CloseIcon
							color={token('color.text.subtle', N500)}
							label={formatMessage(i18n.xButtonLabel)}
						/>
					</Button>
				</Flex>
			</Box>

			<Box xcss={mediaWrapper}>
				{isReducedMotion ? (
					<SplashImage src={staticAdminOptInImg} alt="" data-testid="loom-beta-opt-in-image" />
				) : (
					<SplashVideo
						src={`${PUBLIC_PATH}loom-admin-onboarding-beta-modal-splash.mp4`}
						width="100%"
						data-testid="loom-beta-opt-in-video"
						poster={staticAdminOptInImg}
						autoPlay
						loop
						muted
					/>
				)}
			</Box>

			<Box paddingBlock="space.500" paddingInline="space.600">
				<Flex direction="column" alignItems="center" xcss={centeredText}>
					<Box paddingBlockEnd="space.250" paddingInlineEnd="space.050">
						<LoomLogo appearance="brand" size="xlarge" />
					</Box>
					<Heading size="small">
						<FormattedMessage {...i18n.modalDescription} />
					</Heading>
					<Text>
						<FormattedMessage {...i18n.modalPrompt} />
					</Text>
					<Box paddingBlockStart="space.500">
						<Button
							appearance="primary"
							href={ADMIN_NEW_FEATURES.toUrl()}
							onClick={handleEnableClick}
						>
							<FormattedMessage {...i18n.enableButtonText} />
						</Button>
					</Box>
				</Flex>
			</Box>
		</Modal>
	);
};
